/**
 * Canonical date formatting for the platform.
 *
 * Every user-visible date renders as dd-mm-yyyy (platform standard, user 2026-09-08;
 * previously dd/mm/yyyy). Do NOT call `toLocaleDateString()` /
 * `toLocaleString()` without arguments in views — those follow the *browser's* locale, so the
 * same record shows 06/08/2026 in India and 08/06/2026 in the US. Use these helpers instead.
 *
 * Dates are parsed textually wherever possible: `new Date('2026-08-06')` is parsed as UTC
 * midnight and then printed in local time, which silently shifts a date-only value to the
 * previous day for anyone west of UTC.
 */

const DASH = '—';

/** Matches the leading yyyy-mm-dd of an ISO date or datetime string. */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
/** Matches the time portion of an ISO datetime string. */
const ISO_TIME = /[T ](\d{2}):(\d{2})/;

export type DateInputValue = string | number | Date | number[] | null | undefined;

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * Coerces the shapes the backend actually sends into a `yyyy-mm-dd` string:
 * ISO strings, `Date` objects, epoch millis, and Jackson's `LocalDate` arrays
 * (`[yyyy, mm, dd]`). Returns `''` when the value is empty or unparseable.
 */
export const toIsoDate = (value: DateInputValue): string => {
  if (value === null || value === undefined || value === '') return '';

  if (Array.isArray(value)) {
    const [y, m, d] = value;
    if (!y || !m || !d) return '';
    return `${y}-${pad(m)}-${pad(d)}`;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    // Local getters, not toISOString(), so the date the user sees is the date we emit.
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  if (typeof value === 'number') return toIsoDate(new Date(value));

  const str = String(value).trim();
  if (!str) return '';

  const iso = str.match(ISO_DATE);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  // Already display format (a value round-tripping through a form). BOTH separators are
  // accepted: the platform moved from dd/mm/yyyy to dd-mm-yyyy on 2026-09-08, and any value
  // still carrying a slash (older form state, a pasted value) must keep parsing.
  const dmy = str.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;

  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? '' : toIsoDate(parsed);
};

/**
 * Formats any supported value as `dd/mm/yyyy`.
 *
 * @param value    the date to format
 * @param fallback what to render when the value is empty or unparseable (default `—`)
 */
export const formatDate = (value: DateInputValue, fallback: string = DASH): string => {
  const iso = toIsoDate(value);
  if (!iso) return fallback;
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};

/**
 * Formats as `dd-mm-yyyy HH:mm` (24-hour). Falls back to plain `dd-mm-yyyy` when the value
 * carries no time component, so a date-only string never gains a misleading 00:00.
 */
export const formatDateTime = (value: DateInputValue, fallback: string = DASH): string => {
  const date = formatDate(value, '');
  if (!date) return fallback;

  if (typeof value === 'string') {
    const t = value.match(ISO_TIME);
    if (t) return `${date} ${t[1]}:${t[2]}`;
    if (!/[T ]\d{2}:/.test(value)) return date;
  }

  const parsed = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return date;
  return `${date} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

/** `yyyy-mm-dd` → `dd-mm-yyyy`. Returns `''` for anything unparseable (no em dash). */
export const isoToDisplayDate = (iso?: string | null): string => formatDate(iso, '');

/**
 * `dd-mm-yyyy` → `yyyy-mm-dd`, validating that the date actually exists (rejects 31-02-2026).
 * Accepts `/` as well as `-`, so values typed or stored before the 2026-09-08 separator change
 * still parse — without this, changing the display alone would silently reject every entry.
 * Returns `''` when the text is not a complete, real date — callers use that to decide whether
 * a partially-typed value should be committed.
 */
export const displayDateToIso = (display?: string | null): string => {
  const m = String(display ?? '').trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!m) return '';
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  const probe = new Date(year, month - 1, day);
  if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) {
    return '';
  }
  return `${year}-${pad(month)}-${pad(day)}`;
};
